import Close from "@mui/icons-material/Close"
import { Alert, Box, DialogContent, IconButton, TextField, Typography } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogTitle from "@mui/material/DialogTitle"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useFondoEliminaConSupporti } from "../hooks/fondi"
import { useSnackbarMessage } from "../hooks/snackbar"
import { Fondo } from "../types"

type DialogEliminaFondoConSupportiProps = {
  open: boolean
  handleClose: () => void
  fondo?: Pick<Fondo, "id" | "segnatura" | "denominazione" | "numero_pellicole">
  onDeleted?: () => void
}

export default function DialogEliminaFondoConSupporti({
  open,
  handleClose,
  fondo,
  onDeleted,
}: DialogEliminaFondoConSupportiProps) {
  const { t } = useTranslation()
  const showMessage = useSnackbarMessage()
  const [segnatura, setSegnatura] = useState("")
  const { mutateAsync: elimina, isPending } = useFondoEliminaConSupporti(fondo?.id)

  const segnaturaOk = !!fondo && segnatura.trim() === fondo.segnatura.trim()

  const close = () => {
    setSegnatura("")
    handleClose()
  }

  const onConfirm = () => {
    return elimina({ segnatura: segnatura.trim() })
      .then((res) => {
        close()
        showMessage(
          t("fondo.esito_elimina_con_supporti_ok", {
            supporti: res.supporti_eliminati,
            documenti: res.documenti_eliminati,
          })
        )
        onDeleted?.()
      })
      .catch(() => {
        showMessage(t("general.esito_elimina_errore"), "error")
      })
  }

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={close} scroll="paper">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "semibold" }} component="h1">
            {t("fondo.cta_elimina_con_supporti")}
          </Typography>
          <IconButton aria-label="close" onClick={close}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t("fondo.cta_subtitle_elimina")} <b>"{fondo?.denominazione}"</b> ({fondo?.segnatura})?
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("fondo.supporti_collegati")}: <b>{fondo?.numero_pellicole ?? 0}</b>
          </Typography>
          <Typography variant="body2">{t("fondo.elimina_con_supporti_avviso")}</Typography>
        </Alert>
        <TextField
          label={t("fondo.elimina_con_supporti_conferma", { segnatura: fondo?.segnatura })}
          size="small"
          fullWidth
          autoComplete="off"
          value={segnatura}
          onChange={(e) => setSegnatura(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && segnaturaOk && !isPending) onConfirm()
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          p: 4,
          flexDirection: "row",
          gap: 4,
        }}
      >
        <Button size="large" onClick={close} variant="text" sx={{ width: "100%" }}>
          {t("general.annulla")}
        </Button>
        <Button
          color="error"
          size="large"
          variant="contained"
          disabled={!segnaturaOk || isPending}
          onClick={onConfirm}
          sx={{ width: "100%" }}
        >
          {t("general.elimina")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
